// src/web/Advertisement/AdHome.tsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StartFooterAd from './StartFooterAd';
import HighLogo from '../HighLogo';
import logo from '../../assets/logo.png';

const ROUTES = {
  advertiserLogin: '/ad-login',
  advertiserRegister: '/register',
  privacy: '/legal/privacypolicy',
  terms: '/legal/termsofuse',
  help: '/helpsupportstart',
};

const AdHome: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();


  return (
    <div style={pageStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <button
          type="button"
          onClick={() => navigate('/ads-home')}
          aria-label="Go to ELNYDO homepage"
          style={logoButtonStyle}
        >
          <HighLogo
            src={logo}
            style={{
              width: 180,
              display: 'block',
            }}
            padding={0}
          />
        </button>

        <div style={headerActionsStyle}>
         
          <button
            type="button"
            onClick={() => navigate(ROUTES.advertiserLogin)}
            style={loginButtonStyle}
          >
            {t('home.nav.login', 'Log in')}
          </button>

          <button
            type="button"
            onClick={() => navigate(ROUTES.advertiserRegister)}
            style={registerButtonStyle}
          >
            {t('home.nav.createAccount', 'Create account')}
          </button>
        </div>
      </header>

      {/* Main content */}
      <main style={mainStyle}>
        <section style={heroContentStyle}>
          <div style={badgeStyle}>
            {t('home.ads.badge', 'ELNYDO for advertisers')}
          </div>

          <h1 style={titleStyle}>
            {t(
              'home.ads.heroTitle',
              'Reach people through visual advertising'
            )}
          </h1>

          <p style={subtitleStyle}>
            {t(
              'home.ads.heroSubtitle',
              'Create, manage and submit advertising campaigns designed for the ELNYDO community.'
            )}
          </p>

          <div style={featureListStyle}>
            <div style={featureStyle}>
              <span style={featureIconStyle}>✓</span>

              <span>
                {t(
                  'home.ads.features.create',
                  'Create image and video advertisements'
                )}
              </span>
            </div>

            <div style={featureStyle}>
              <span style={featureIconStyle}>✓</span>

              <span>
                {t(
                  'home.ads.features.review',
                  'Submit campaigns for review before publication'
                )}
              </span>
            </div>

            <div style={featureStyle}>
              <span style={featureIconStyle}>✓</span>

              <span>
                {t(
                  'home.ads.features.manage',
                  'Manage your advertisements from one dashboard'
                )}
              </span>
            </div>
          </div>

          <div style={ctaRowStyle}>
            <button
              type="button"
              onClick={() => navigate(ROUTES.advertiserRegister)}
              style={primaryCtaStyle}
            >
              {t('home.nav.createAccount')}
            </button>

            <button
              type="button"
              onClick={() => navigate(ROUTES.advertiserLogin)}
              style={secondaryCtaStyle}
            >
             {t('home.nav.login', 'Log in')}
            </button>
          </div>

          <p style={accountNoteStyle}>
            {t(
              'home.ads.accountNote',
              'Your advertiser access uses the same ELNYDO account system.'
            )}
          </p>
        </section>

        {/* Advertisement preview */}
        <section style={previewSideStyle}>
          <div style={glowStyle} />

          <div style={phoneFrameStyle}>
            <div style={phoneSpeakerStyle} />

            <div style={phoneScreenStyle}>
              <video
                autoPlay
                muted
                loop
                playsInline
                poster="/images/setup-ads.webp"
                style={previewMediaStyle}
              >
                <source src="/videos/ads-demo.mp4" type="video/mp4" />
              </video>

              <div style={previewOverlayStyle}>
                <div style={sponsoredLabelStyle}>
                  {t('home.ads.sponsored', 'Sponsored')}
                </div>

                <div style={previewTitleStyle}>
                  {t(
                    'home.ads.previewTitle',
                    'Your campaign. Your audience.'
                  )}
                </div>

                <div style={previewDescriptionStyle}>
                  {t(
                    'home.ads.previewDescription',
                    'Visual advertising integrated into the ELNYDO experience.'
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

     <StartFooterAd />
    </div>
  );
};

const pageStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  overflowX: 'hidden',
  background:
    'radial-gradient(circle at 75% 25%, rgba(59,130,246,0.16), transparent 30%), radial-gradient(circle at 25% 70%, rgba(249,115,22,0.11), transparent 30%), #020617',
  color: '#f8fafc',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const headerStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 82,
  padding: '14px clamp(20px, 5vw, 72px)',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 20,
  borderBottom: '1px solid rgba(148,163,184,0.14)',
  background: 'rgba(2,6,23,0.72)',
  backdropFilter: 'blur(18px)',
  position: 'relative',
  zIndex: 10,
};

const logoButtonStyle: React.CSSProperties = {
  padding: 0,
  margin: 0,
  marginTop: '10px',
  marginLeft: 'clamp(-28px, -2vw, -10px)',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
};

const headerActionsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: 10,
};


const loginButtonStyle: React.CSSProperties = {
  minHeight: 40,
  padding: '8px 18px',
  borderRadius: 999,
  border: '1px solid rgba(148,163,184,0.4)',
  background: 'rgba(15,23,42,0.45)',
  color: '#f8fafc',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
};

const registerButtonStyle: React.CSSProperties = {
  minHeight: 40,
  padding: '8px 18px',
  borderRadius: 999,
  border: 'none',
  background:
    'linear-gradient(135deg, #60a5fa 0%, #f97316 55%, #facc15 100%)',
  color: '#020617',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 800,
  boxShadow: '0 10px 30px rgba(249,115,22,0.2)',
};

const mainStyle: React.CSSProperties = {
  flex: 1,
  width: '100%',
  maxWidth: 1240,
  margin: '0 auto',
  padding: 'clamp(44px, 7vh, 86px) clamp(24px, 5vw, 72px)',
  boxSizing: 'border-box',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.1fr) minmax(300px, 0.9fr)',
  alignItems: 'center',
  gap: 'clamp(50px, 8vw, 110px)',
};

const heroContentStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 650,
};

const badgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '7px 13px',
  marginBottom: 22,
  borderRadius: 999,
  border: '1px solid rgba(96,165,250,0.35)',
  background: 'rgba(37,99,235,0.12)',
  color: '#93c5fd',
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: 0.2,
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  maxWidth: 650,
  fontSize: 'clamp(42px, 5.4vw, 72px)',
  lineHeight: 1.02,
  letterSpacing: '-0.055em',
  fontWeight: 850,
  background:
    'linear-gradient(135deg, #f8fafc 10%, #bfdbfe 45%, #fed7aa 75%, #fef08a 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
};

const subtitleStyle: React.CSSProperties = {
  maxWidth: 590,
  margin: '24px 0 0',
  color: '#aeb9ca',
  fontSize: 'clamp(17px, 1.6vw, 20px)',
  lineHeight: 1.65,
};

const featureListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 13,
  marginTop: 28,
};

const featureStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 11,
  color: '#dbe4f0',
  fontSize: 14,
  lineHeight: 1.5,
};

const featureIconStyle: React.CSSProperties = {
  width: 23,
  height: 23,
  flexShrink: 0,
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(34,197,94,0.13)',
  border: '1px solid rgba(34,197,94,0.3)',
  color: '#4ade80',
  fontSize: 12,
  fontWeight: 900,
};

const ctaRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 13,
  marginTop: 34,
};

const primaryCtaStyle: React.CSSProperties = {
  minHeight: 52,
  padding: '13px 25px',
  border: 'none',
  borderRadius: 999,
  background:
    'linear-gradient(135deg, #60a5fa 0%, #f97316 55%, #facc15 100%)',
  color: '#020617',
  cursor: 'pointer',
  fontSize: 15,
  fontWeight: 850,
  boxShadow: '0 16px 40px rgba(249,115,22,0.24)',
};

const secondaryCtaStyle: React.CSSProperties = {
  minHeight: 52,
  padding: '12px 24px',
  borderRadius: 999,
  border: '1px solid rgba(148,163,184,0.38)',
  background: 'rgba(15,23,42,0.48)',
  color: '#f8fafc',
  cursor: 'pointer',
  fontSize: 15,
  fontWeight: 700,
};

const accountNoteStyle: React.CSSProperties = {
  margin: '16px 0 0',
  color: '#64748b',
  fontSize: 12.5,
  lineHeight: 1.5,
};

const previewSideStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const glowStyle: React.CSSProperties = {
  position: 'absolute',
  width: 390,
  height: 390,
  maxWidth: '85vw',
  maxHeight: '85vw',
  borderRadius: '50%',
  background:
    'radial-gradient(circle, rgba(249,115,22,0.19), rgba(59,130,246,0.1) 48%, transparent 72%)',
  filter: 'blur(15px)',
};


const previewMediaStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'block',
  objectFit: 'cover',
};

const previewOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  insetInline: 0,
  bottom: 0,
  padding: '70px 18px 20px',
  background:
    'linear-gradient(to top, rgba(0,0,0,0.96), rgba(0,0,0,0.58) 55%, transparent)',
};

const sponsoredLabelStyle: React.CSSProperties = {
  display: 'inline-flex',
  marginBottom: 8,
  padding: '4px 8px',
  borderRadius: 999,
  background: 'rgba(255,255,255,0.14)',
  color: '#e5e7eb',
  fontSize: 10,
  fontWeight: 700,
};

const previewTitleStyle: React.CSSProperties = {
  color: '#ffffff',
  fontSize: 17,
  lineHeight: 1.25,
  fontWeight: 800,
};

const previewDescriptionStyle: React.CSSProperties = {
  marginTop: 5,
  color: '#d1d5db',
  fontSize: 11.5,
  lineHeight: 1.45,
};

const phoneFrameStyle: React.CSSProperties = {
  width: 285,
  height: 585,
  borderRadius: 42,
  padding: 12,
  background: "linear-gradient(145deg, #111827, #020617)",
  border: "1px solid rgba(248,250,252,0.18)",
  boxShadow:
    "0 35px 90px rgba(0,0,0,0.85), inset 0 0 0 1px rgba(255,255,255,0.08)",
  position: "relative",
  zIndex: 2,
};

const phoneSpeakerStyle: React.CSSProperties = {
  position: "absolute",
  top: 18,
  left: "50%",
  transform: "translateX(-50%)",
  width: 78,
  height: 6,
  borderRadius: 999,
  background: "rgba(148,163,184,0.45)",
  zIndex: 2,
};

const phoneScreenStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  borderRadius: 32,
  overflow: "hidden",
  background:
    "radial-gradient(circle at top, rgba(212,175,55,0.28), rgba(15,23,42,1) 40%, #000 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
};

export default AdHome;